import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { requisitarAtivacao } from '@/hooks/useAtivacaoLinhas';

type EtapaIsabella = 'ativacao_esim' | 'boas_vindas' | 'fatura_1';
interface RondaIsabella {
  ligada: boolean; horario: string; hora_fim: number; por_rodada: number; pode_ligar: boolean;
  hoje: { atendidos: number; pela_conversa: number; por_outra_linha: number; pulados: number; motivos: Array<{ motivo: string; n: number }> };
}
interface ResumoIsabellaCiclo { etapa: EtapaIsabella; acoes: number; concluidos: number; clientes: number; pedidos_ao_bee?: number; ronda?: RondaIsabella | null }

const PERIODO = (dias: number) => (dias === 1 ? 'hoje' : dias === 7 ? 'nos 7 dias' : 'no mês');
const VERDE = 'text-[#075E54] dark:text-[#25D366]';

function Ponto({ ligada }: { ligada: boolean }) {
  return <span aria-hidden="true" className={`mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${ligada
    ? 'bg-[#075E54] shadow-[0_0_0_3px_rgba(7,94,84,0.16)] dark:bg-[#25D366]' : 'bg-[#E88A8A]'}`} />;
}

function Linha({ rotulo, valor, recuo, tom }: { rotulo: string; valor: string | number; recuo?: boolean; tom?: string }) {
  return <div className={`flex items-center justify-between gap-3 border-t border-border/50 py-1 text-[11.5px] ${recuo ? 'pl-2.5' : ''}`}>
    <span className={recuo ? 'text-muted-foreground' : ''}>{rotulo}</span>
    <span className={`shrink-0 tabular-nums ${tom || (recuo ? '' : 'text-muted-foreground')}`}>{valor}</span>
  </div>;
}

/**
 * O QUE A IsabellA AJUDOU A CONCLUIR NO CICLO (Douglas 03/10/2026): um card em
 * cada triagem. O ciclo é um só, o da automação: conta o que o cliente concluiu
 * pela pergunta da automação com ela no atendimento, e a conta que ela entregou
 * (Faturas). A régua mora no backend: isabella-no-ciclo.service.ts.
 * A ronda dela (CNPJ) aparece no card e liga POR TRIAGEM, só pelo master
 * (ronda-ciclo.service.ts). `compacto` é o selo da linha "Você vê" (Boas-vindas
 * e Faturas, Douglas 03/10/2026: o quadrado ocupava uma linha inteira).
 */
export function CardIsabellaCiclo({ etapa, segmento, dias, compacto }: { etapa: EtapaIsabella; segmento: string; dias: number; compacto?: boolean }) {
  const chave = ['triagem-isabella-ciclo', etapa, segmento, dias];
  const qc = useQueryClient();
  const consulta = useQuery({ queryKey: chave,
    queryFn: () => requisitarAtivacao<ResumoIsabellaCiclo>(`isabella/ciclo?etapa=${etapa}&segmento=${segmento}&dias=${dias}`), refetchInterval: 60_000 });
  const chaveRonda = useMutation({
    mutationFn: (ligada: boolean) => requisitarAtivacao<RondaIsabella>('isabella/ronda', { etapa, ligada }),
    onSuccess: (ronda) => {
      qc.setQueryData<ResumoIsabellaCiclo>(chave, (r) => (r ? { ...r, ronda } : r));
      qc.invalidateQueries({ queryKey: ['triagem-isabella-ciclo', etapa] });
      toast.success(ronda.ligada ? 'Ronda da IsabellA ligada nesta triagem' : 'Ronda da IsabellA desligada nesta triagem');
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const r = consulta.data;
  const ronda = r?.ronda ?? null;
  const fatura = etapa === 'fatura_1';
  const rotulo = fatura
    ? `IsabellA entregou a conta ${PERIODO(dias)} · pediu ${r?.pedidos_ao_bee ?? '—'} ao Bee`
    : `IsabellA ajudou a concluir ${PERIODO(dias)} · ${r?.acoes ?? '—'} reenvios dela`;
  const unidade = fatura ? 'clientes' : etapa === 'ativacao_esim' ? 'linhas' : 'pedidos';
  const regra = fatura
    ? 'Clientes do ciclo de faturas a quem a IsabellA entregou a conta no WhatsApp, e quantas contas ela pediu à Claro com prioridade.'
    : etapa === 'ativacao_esim'
      ? 'Linhas que o cliente validou respondendo 2 à pergunta da automação, com a IsabellA no atendimento dele nas 24 horas antes. Reenvios: perguntas que ela reenviou pela automação (desde 03/10/2026).'
      : 'Pedidos que fecharam a Boas-vindas com a nota dada à pergunta da automação, com a IsabellA no atendimento dele nas 24 horas antes. Reenvios: perguntas que ela reenviou pela automação (desde 03/10/2026).';

  const numero = <strong className={`font-display tabular-nums ${VERDE}`}>{r?.concluidos ?? '—'}</strong>;
  const selo = consulta.isError ? 'IsabellA no ciclo · não carregou'
    : <>IsabellA {fatura ? <>entregou a conta a {numero} clientes</> : <>{numero} {unidade}</>} {PERIODO(dias)}
      {ronda ? <> · {ronda.ligada ? `ronda até ${ronda.hora_fim}h` : 'ronda desligada'}</> : null}</>;
  const classeSelo = 'ml-auto inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-primary/40 px-2 py-0.5 text-[10.5px] text-muted-foreground';
  if (compacto && !ronda) return <span className={classeSelo} title={regra}>{selo}</span>;

  const corpo = <>
    <p className="truncate text-[10px] text-muted-foreground">{consulta.isError ? 'IsabellA no ciclo · não carregou' : rotulo}</p>
    <p className={`font-display text-[16px] font-bold tabular-nums ${VERDE}`}>
      {r?.concluidos ?? '—'} <span className="text-[11px] font-normal">{unidade}</span>
    </p>
  </>;
  if (!ronda) return <div className="rounded-md border border-border/60 px-2.5 py-1.5" title={regra}>{corpo}</div>;

  return <Popover>
    <PopoverTrigger asChild>
      {compacto
        ? <button type="button" title={regra} className={`${classeSelo} hover:border-primary hover:text-primary`}>
            <Ponto ligada={ronda.ligada} />{selo}<ChevronRight className="h-3 w-3" aria-hidden="true" />
          </button>
        : <button type="button" title={regra} className="min-w-0 rounded-md border border-border/60 px-2.5 py-1.5 text-left hover:border-primary">
            {corpo}
            <p className="mt-0.5 flex items-center truncate text-[10px] text-muted-foreground">
              <Ponto ligada={ronda.ligada} />{ronda.ligada ? `Ronda ligada · até ${ronda.hora_fim}h` : 'Ronda desligada'}
            </p>
          </button>}
    </PopoverTrigger>
    <PopoverContent align="end" className="w-[340px] p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="flex items-center text-[12.5px] font-semibold">
          <Ponto ligada={ronda.ligada} />Ronda da IsabellA · {ronda.ligada ? 'ligada' : 'desligada'}
        </span>
        {ronda.pode_ligar && <button type="button" role="switch" aria-checked={ronda.ligada} disabled={chaveRonda.isPending}
          aria-label={ronda.ligada ? 'Desligar a ronda nesta triagem' : 'Ligar a ronda nesta triagem'}
          onClick={() => chaveRonda.mutate(!ronda.ligada)}
          className={`relative h-[18px] w-[34px] flex-shrink-0 rounded-full transition-colors ${ronda.ligada ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
          <span className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-all ${ronda.ligada ? 'left-[18px]' : 'left-[2px]'}`} />
        </button>}
      </div>
      <Linha rotulo="Horário" valor={ronda.horario} />
      <Linha rotulo="Ritmo" valor={`${ronda.por_rodada} clientes a cada 15 min`} />
      <Linha rotulo="Hoje: atendidos" valor={ronda.hoje.atendidos} tom={`font-semibold ${VERDE}`} />
      <Linha recuo rotulo="pela conversa dele" valor={ronda.hoje.pela_conversa} />
      <Linha recuo rotulo="pela reserva da equipe do dono" valor={ronda.hoje.por_outra_linha} />
      <Linha rotulo="Hoje: pulados" valor={ronda.hoje.pulados} tom="font-semibold text-primary" />
      {ronda.hoje.motivos.map(m => <Linha key={m.motivo} recuo rotulo={m.motivo} valor={m.n} />)}
    </PopoverContent>
  </Popover>;
}
